import React, { useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { makeStyles } from "@material-ui/core/styles";
import CustomPhoneNumber from "../elements/phoneInput";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signup } from "../../services/auth";
import { formatError, outerLink } from "../../utils/functions";
import { IoArrowBackCircle } from "react-icons/io5";

const validationSchema = Yup.object().shape({
  fullname: Yup.string().required("Fullname is required"),
  email: Yup.string().required("Email is required").email("Email is invalid"),
  phone: Yup.string().required("Phone is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .max(40, "Password must not exceed 40 characters")
    .matches(
      "^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$",
      "Password must include at least one uppercase letter, one lowercase letter, one number and one special character"
    ),
  confirmPassword: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("password"), null], "Confirm Password does not match"),
  acceptTerms: Yup.bool().oneOf([true], "Accept Terms is required"),
});

const useStyles = makeStyles((theme) => ({
  helper: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.7rem",
    color: "red",
  },
  box: {
    padding: "1rem 10rem 1rem 5rem",
    [theme.breakpoints.down("sm")]: {
      padding: "1rem 2rem",
    },
  },
}));

const environment = process.env.NODE_ENV;

const oauth =
  environment === "development"
    ? process.env.REACT_APP_BACKEND_DEV
    : process.env.REACT_APP_URL;

const SignupForm = ({ setOtp, error, setError }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const onSubmit = useCallback(
    async (data) => {
      let response = await dispatch(signup(data, { loader: true }));
      if (response === true) {
        setOtp(data?.email);
      } else {
        setError(response);
      }
    },
    [dispatch, setOtp, setError]
  );
  let myListnerEvent = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSubmit(onSubmit)();
      }
    },
    [handleSubmit, onSubmit]
  );

  useEffect(() => {
    window.addEventListener("keypress", myListnerEvent, false);
    return () => {
      window.removeEventListener("keypress", myListnerEvent, false);
    };
  }, [myListnerEvent]);
  return (
    <div>
      <Box className={classes.box}>
        <IoArrowBackCircle
          className="back-btn"
          onClick={() => window.history.back()}
        />
        <div className="signup-heading">
          To New <br /> Beginnings
        </div>
        <div className="error-message">{formatError(error)}</div>
        <Grid container spacing={0} style={{ gridGap: "10px" }}>
          <Grid item xs={12} sm={12}>
            <TextField
              required
              id="fullname"
              variant="outlined"
              name="fullname"
              label="Full Name"
              fullWidth
              margin="dense"
              {...register("fullname")}
              error={errors.fullname ? true : false}
            />
            <Typography
              variant="inherit"
              color="textSecondary"
              className={classes.helper}
            >
              {errors.fullname?.message}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              required
              variant={"outlined"}
              id="email"
              name="email"
              label="Work Email"
              fullWidth
              margin="dense"
              {...register("email")}
              error={errors.email ? true : false}
            />
            <Typography
              variant="inherit"
              color="textSecondary"
              className={classes.helper}
            >
              {errors.email?.message}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Controller
              name="phone"
              control={control}
              inputRef={register()}
              render={({ field: { name, value, onChange, onBlur } }) => {
                return (
                  <PhoneInput
                    placeholder="Enter phone number"
                    id="phone"
                    name={name}
                    onBlur={onBlur}
                    value={value}
                    onChange={onChange}
                    error={errors?.phone ? true : false}
                    inputComponent={CustomPhoneNumber}
                  />
                );
              }}
            />

            <Typography
              variant="inherit"
              color="textSecondary"
              className={classes.helper}
            >
              {errors.phone?.message}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              required
              id="password"
              name="password"
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              margin="dense"
              {...register("password")}
              error={errors.password ? true : false}
            />
            <Typography
              variant="inherit"
              color="textSecondary"
              className={classes.helper}
            >
              {errors.password?.message}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              required
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              fullWidth
              variant="outlined"
              margin="dense"
              {...register("confirmPassword")}
              error={errors.confirmPassword ? true : false}
            />
            <Typography
              variant="inherit"
              color="textSecondary"
              className={classes.helper}
            >
              {errors.confirmPassword?.message}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              id="referral"
              variant="outlined"
              name="referral"
              label="Referral Code"
              fullWidth
              margin="dense"
              {...register("referral")}
              error={errors.referral ? true : false}
            />
            <Typography
              variant="inherit"
              color="textSecondary"
              className={classes.helper}
            >
              {errors.referral?.message}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Controller
                  control={control}
                  name="acceptTerms"
                  defaultValue="false"
                  inputRef={register()}
                  render={({ field: { onChange } }) => (
                    <Checkbox
                      color="primary"
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />
              }
              label={
                <Typography
                  color={errors.acceptTerms ? "error" : "inherit"}
                  style={{ fontSize: "0.7rem" }}
                >
                  I agree to the{" "}
                  <Link to={"/terms-of-use"}>
                    <span className="terms-of-use"> Terms of Service</span>
                  </Link>{" "}
                  and{" "}
                  <Link to={"/privacy-policy"}>
                    <span className="terms-of-use"> privacy policy</span>
                  </Link>
                </Typography>
              }
            />
            <br />
            <Typography
              variant="inherit"
              color="textSecondary"
              className={classes.helper}
            >
              {errors.acceptTerms ? "(" + errors.acceptTerms.message + ")" : ""}
            </Typography>
          </Grid>
        </Grid>
        <Box mt={3}>
          <button onClick={handleSubmit(onSubmit)} className="signup-button">
            Register
          </button>
        </Box>
        <Box className="text-help">or</Box>
        <Box>
          <button
            onClick={() => {
              outerLink(`${oauth}/auth/oauth`, "_self");
            }}
            className="signup-button google-button"
          >
            <img
              src={require("../../assets/images/google.svg")}
              alt="google sign in"
            />
            Sign in with Google (Work Email)
          </button>
        </Box>
        <Box mt={3}>
          <div className="signup-already">
            Already have an account?{" "}
            <Link to={"/onestack-data/login"}>
              {" "}
              <span className="black">Login</span>
            </Link>
          </div>
        </Box>
      </Box>
    </div>
  );
};

export default SignupForm;
